import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const DEFAULT_MENU_CATEGORIES = ['Наборы', 'десерты', 'напитки'];

const CATEGORY_EMOJIS = {
    'Наборы': '🍱',
    десерты: '🍰',
    напитки: '☕'
};
const MenuPanel = ({ token }) => {
    const [menuItems, setMenuItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [filterCategory, setFilterCategory] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        category: '',
        is_available: true
    });

    const apiFetch = (url, options = {}) => fetch(url, {
        ...options,
        headers: {
            ...(options.headers || {}),
            Authorization: `Bearer ${token}`
        }
    });

    const categoryOptions = categories.length > 0 ? categories : DEFAULT_MENU_CATEGORIES;

    // Загрузка блюд
    const fetchMenuItems = async () => {
        try {
            const url = filterCategory
                ? `${API_URL}/api/menu/items?category=${filterCategory}`
                : `${API_URL}/api/menu/items`;

            const response = await apiFetch(url);
            const data = await response.json();

            if (data.success) {
                setMenuItems(data.items);
            }
        } catch (error) {
            console.error('Error fetching menu items:', error);
        }
    };

    // Загрузка категорий из справочника настроек
    const fetchCategories = async () => {
        try {
            const settingsResponse = await apiFetch(`${API_URL}/api/settings/options/menu_categories`);
            const settingsData = await settingsResponse.json();

            if (settingsData.success) {
                const settingsCategories = (settingsData.options || []).map((option) => option.label || option.value);
                setCategories(settingsCategories);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    useEffect(() => {
        fetchMenuItems();
        fetchCategories();
    }, [filterCategory]);

    // Открыть диалог для добавления
    const handleOpenDialog = () => {
        setEditingItem(null);
        setSaveError('');
        setFormData({
            name: '',
            description: '',
            price: '',
            category: '',
            is_available: true
        });
        setOpenDialog(true);
    };

    // Открыть диалог для редактирования
    const handleEditItem = (item) => {
        setEditingItem(item);
        setSaveError('');
        setFormData({
            name: item.name,
            description: item.description || '',
            price: item.price,
            category: item.category,
            is_available: item.is_available
        });
        setOpenDialog(true);
    };

    // Сохранить блюдо
    const handleSaveItem = async () => {
        const name = formData.name.trim();
        const category = formData.category.trim();
        const price = parseFloat(formData.price);

        if (!name) {
            setSaveError('Укажите название блюда');
            return;
        }
        if (!category) {
            setSaveError('Выберите категорию');
            return;
        }
        if (Number.isNaN(price) || price < 0) {
            setSaveError('Укажите корректную цену');
            return;
        }

        setSaving(true);
        setSaveError('');

        try {
            const url = editingItem
                ? `${API_URL}/api/menu/items/${editingItem.id}`
                : `${API_URL}/api/menu/items`;

            const method = editingItem ? 'PUT' : 'POST';
            const payload = {
                name,
                description: formData.description?.trim() || null,
                price,
                category,
                is_available: formData.is_available !== false
            };

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            let data;
            try {
                data = await response.json();
            } catch {
                setSaveError('Сервер вернул некорректный ответ');
                return;
            }

            if (!response.ok || !data.success) {
                setSaveError(data.error || `Не удалось сохранить блюдо (${response.status})`);
                return;
            }

            if (category) {
                await apiFetch(`${API_URL}/api/settings/options/menu_categories`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ value: category, label: category })
                });
            }

            setOpenDialog(false);
            fetchMenuItems();
            fetchCategories();
        } catch (error) {
            console.error('Error saving menu item:', error);
            setSaveError('Ошибка сети при сохранении блюда');
        } finally {
            setSaving(false);
        }
    };

    // Удалить блюдо
    const handleDeleteItem = async (id) => {
        if (!window.confirm('Удалить это блюдо?')) return;

        try {
            const response = await apiFetch(`${API_URL}/api/menu/items/${id}`, {
                method: 'DELETE'
            });

            const data = await response.json();

            if (data.success) {
                fetchMenuItems();
            }
        } catch (error) {
            console.error('Error deleting menu item:', error);
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>Меню</Typography>

            <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h5">Справочник блюд</Typography>
                        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
                            Добавить блюдо
                        </Button>
                    </Box>

                    {/* Фильтры */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Категория</InputLabel>
                                        <Select
                                            value={filterCategory}
                                            label="Категория"
                                            onChange={(e) => setFilterCategory(e.target.value)}
                                        >
                                            <MenuItem value="">Все категории</MenuItem>
                                            {categories.map((cat) => (
                                                <MenuItem key={cat} value={cat}>
                                                    {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip label={`Всего блюд: ${menuItems.length}`} color="primary" />
                                        <Chip
                                            label={`Доступно: ${menuItems.filter(i => i.is_available).length}`}
                                            color="success"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Таблица блюд */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Название</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Описание</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Категория</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Цена</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {menuItems.map((item) => (
                                    <TableRow key={item.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                {item.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.description || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.category}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                                ₽{parseFloat(item.price).toLocaleString('ru-RU')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={item.is_available ? 'Доступно' : 'Недоступно'}
                                                size="small"
                                                color={item.is_available ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => handleEditItem(item)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteItem(item.id)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {menuItems.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <Typography color="text.secondary">
                                Нет блюд в справочнике
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleOpenDialog}
                                sx={{ mt: 2 }}
                            >
                                Добавить первое блюдо
                            </Button>
                        </Box>
                    )}

                    {/* Диалог добавления/редактирования */}
                    <Dialog
                        open={openDialog}
                        onClose={() => setOpenDialog(false)}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>
                            {editingItem ? 'Редактировать блюдо' : 'Добавить блюдо'}
                        </DialogTitle>
                        <DialogContent>
                            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {saveError && (
                                    <Alert severity="error" onClose={() => setSaveError('')}>
                                        {saveError}
                                    </Alert>
                                )}

                                <TextField
                                    label="Название"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    fullWidth
                                    required
                                />

                                <TextField
                                    label="Описание"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />

                                <TextField
                                    label="Цена (₽)"
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    fullWidth
                                    required
                                    inputProps={{ min: 0, step: 0.01 }}
                                />

                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Категория *
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {categoryOptions.map((cat) => {
                                            const isSelected = formData.category === cat;
                                            return (
                                                <Button
                                                    key={cat}
                                                    type="button"
                                                    variant={isSelected ? 'contained' : 'outlined'}
                                                    onClick={() => setFormData({ ...formData, category: cat })}
                                                    sx={{
                                                        minWidth: 72,
                                                        width: 72,
                                                        height: 72,
                                                        p: 0.5,
                                                        flexDirection: 'column',
                                                        borderRadius: 1.5,
                                                        textTransform: 'none',
                                                        lineHeight: 1.2
                                                    }}
                                                >
                                                    <Typography variant="h6" sx={{ fontSize: '1.25rem', mb: 0.25 }}>
                                                        {CATEGORY_EMOJIS[cat] || '🍽'}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                        {cat}
                                                    </Typography>
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                    {!formData.category && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            Выберите категорию
                                        </Typography>
                                    )}
                                </Box>

                                <FormControl fullWidth>                                    <InputLabel>Статус</InputLabel>
                                    <Select
                                        value={formData.is_available}
                                        label="Статус"
                                        onChange={(e) => setFormData({ ...formData, is_available: e.target.value })}
                                    >
                                        <MenuItem value={true}>Доступно</MenuItem>
                                        <MenuItem value={false}>Недоступно</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenDialog(false)} disabled={saving}>Отмена</Button>
                            <Button
                                type="button"
                                variant="contained"
                                onClick={handleSaveItem}
                                disabled={saving}
                            >                                {saving ? 'Сохранение...' : (editingItem ? 'Сохранить' : 'Добавить')}
                            </Button>
                        </DialogActions>
                    </Dialog>
            </Box>
        </Box>
    );
};

export default MenuPanel;
